import { useEffect } from 'react';
import {
  ArrowUpRight,
  CheckSquare,
  Clipboard,
  Clock,
  FlaskConical,
  Keyboard,
  Mail,
  Moon,
  Navigation,
  Plus,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  Sun,
  Tags,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { AI_MODEL_OPTIONS, resolveAiModelOption } from '@oneup/shared';
import {
  DesignSystemToggle,
  useDesignSystem,
} from '@command-center/oneup-modern-ui';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTheme, type Theme } from '@/components/theme-provider';
import { CopilotUsageCard } from '@/components/copilot-usage-card';
import { useAiSettingsStore } from '@/stores/ai-settings';
import { communicationsApi, type GmailIntegrationStatus } from '@/lib/api';
import { useFieldOptions } from '@/lib/use-field-options';
import { cn } from '@/lib/utils';

const shortcutGroups = [
  {
    title: 'Global',
    icon: Keyboard,
    rows: [
      ['Cmd/Ctrl + K', 'Open command palette'],
      ['/', 'Open command palette in command mode'],
      ['C', 'Open Quick Capture'],
      ['V', 'Start Alice voice dictation'],
      ['Cmd/Ctrl + Shift + V', 'Voice dictation (works while typing)'],
      ['T', 'Go to Tasks'],
      ['S', 'Go to Settings'],
      ['Esc', 'Close active dialog or drawer'],
    ],
  },
  {
    title: 'Task Drawer',
    icon: CheckSquare,
    rows: [
      ['E', 'Focus the first editable field'],
      ['U', 'Review Update'],
      ['L', 'Focus Quick Log'],
      ['D', 'Trigger Close Task flow'],
    ],
  },
  {
    title: 'Palette Commands',
    icon: Search,
    rows: [
      ['/new task', 'Open Quick Capture'],
      ['/log time', 'Log time on the active task'],
      ['/send update', 'Review update for the active task'],
      ['/copy id', 'Copy active task record id'],
      ['/today', 'Go to today queue'],
      ['/jump client', 'Filter tasks by client'],
      ['/goto ...', 'Jump to a module'],
    ],
  },
] as const;

const themes: Array<{ value: Theme; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Navigation },
];

const GMAIL_OAUTH_START_URL = '/api/oauth/google/start';

export function SettingsView() {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const flagOptions = useFieldOptions('Command Center Flags');
  const [searchParams] = useSearchParams();
  const gmailCallbackStatus = searchParams.get('gmail');
  const gmailStatusQuery = useQuery({
    queryKey: ['communications', 'gmail-status'],
    queryFn: communicationsApi.gmailStatus,
    staleTime: 15_000,
  });
  const refetchGmailStatus = gmailStatusQuery.refetch;
  const gmailStatus = gmailStatusQuery.data;

  useEffect(() => {
    if (gmailCallbackStatus) void refetchGmailStatus();
  }, [gmailCallbackStatus, refetchGmailStatus]);

  return (
    <div className="space-y-4">
      <DesignSystemCard />

      <AiSettingsCard />

      {/* Pass 4 Track 4 — Copilot daily usage card. Refreshes every 60s. */}
      <CopilotUsageCard />

      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">Appearance</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Theme preference is saved on this device.
            </p>
          </div>
          <Badge variant="muted">Current: {resolvedTheme}</Badge>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {themes.map((option) => {
            const Icon = option.icon;
            const active = theme === option.value;
            return (
              <Button
                key={option.value}
                type="button"
                variant={active ? 'default' : 'outline'}
                size="sm"
                className={cn('h-8 gap-1.5', active && 'pointer-events-none')}
                onClick={() => setTheme(option.value)}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {option.label}
              </Button>
            );
          })}
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <Mail className="h-4 w-4 text-muted-foreground" aria-hidden />
              Integrations
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Connect Gmail for tagged review imports and outgoing email workflows.
            </p>
          </div>
          <Badge variant={gmailStatusBadgeVariant(gmailStatus, gmailStatusQuery.isLoading)}>
            {gmailStatusBadgeText(gmailStatus, gmailStatusQuery.isLoading)}
          </Badge>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button asChild size="sm" className="h-8 gap-1.5">
            <a href={GMAIL_OAUTH_START_URL}>
              <Mail className="h-3.5 w-3.5" aria-hidden />
              {gmailStatus?.connected ? 'Reconnect Gmail' : 'Connect Gmail'}
            </a>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() => void gmailStatusQuery.refetch()}
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            Refresh
          </Button>
          <span className="text-[12px] text-muted-foreground">
            {gmailStatus?.connectedEmail
              ? `Account: ${gmailStatus.connectedEmail}`
              : gmailStatus?.reconnectRequired
                ? 'Reconnect required'
                : 'No persisted Gmail connection'}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
          <span>Imports label: Command Center/Review</span>
          <span>Sync: {gmailStatus?.syncEnabled ? 'enabled' : 'disabled'}</span>
          {gmailStatus?.lastSync && (
            <span>
              Last sync: {gmailStatus.lastSync.status}
              {gmailStatus.lastSync.finishedAt
                ? ` at ${formatSettingsDate(gmailStatus.lastSync.finishedAt)}`
                : ''}
            </span>
          )}
          {gmailStatusQuery.isError && <span>Gmail status unavailable</span>}
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <Tags className="h-4 w-4 text-muted-foreground" aria-hidden />
              Command Center Flags
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Live Airtable multiselect choices used by task tags, Today cards, and future Whiteboard filters.
            </p>
          </div>
          <Badge variant={flagOptions.isError ? 'warning' : 'muted'}>
            {flagOptions.isLoading ? 'Loading' : flagOptions.isError ? 'Schema unavailable' : `${flagOptions.data?.options.length ?? 0} options`}
          </Badge>
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(flagOptions.data?.options ?? []).map((option) => (
            <Badge key={option.name} variant="outline">
              {option.name}
            </Badge>
          ))}
          {!flagOptions.isLoading && !flagOptions.isError && flagOptions.data?.options.length === 0 && (
            <span className="text-[12px] text-muted-foreground">No options returned from Airtable.</span>
          )}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Add new options from any task flag picker by typing the new tag. Removing global Airtable options still belongs in Airtable for now.
        </p>
      </Card>

      <div className="grid gap-4 xl:grid-cols-3">
        {shortcutGroups.map((group) => {
          const Icon = group.icon;
          return (
            <Card key={group.title} className="overflow-hidden">
              <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-3">
                <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                <h2 className="text-sm font-semibold">{group.title}</h2>
              </div>
              <dl className="divide-y divide-border">
                {group.rows.map(([keys, action]) => (
                  <div key={keys} className="grid grid-cols-[128px_1fr] gap-3 px-4 py-2.5">
                    <dt>
                      <ShortcutKeys>{keys}</ShortcutKeys>
                    </dt>
                    <dd className="text-[13px] text-muted-foreground">{action}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          );
        })}
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New work starts with Quick Capture.
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" aria-hidden />
            Time shortcuts require an open task drawer.
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Send className="h-3.5 w-3.5" aria-hidden />
            Send Update uses the Make Orchestrator.
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clipboard className="h-3.5 w-3.5" aria-hidden />
            Copy commands target the active task.
          </span>
        </div>
      </Card>
    </div>
  );
}

function gmailStatusBadgeVariant(
  status: GmailIntegrationStatus | undefined,
  isLoading: boolean,
): 'success' | 'warning' | 'muted' {
  if (isLoading) return 'muted';
  if (status?.connected) return 'success';
  if (status?.reconnectRequired || status?.error) return 'warning';
  return 'muted';
}

function gmailStatusBadgeText(
  status: GmailIntegrationStatus | undefined,
  isLoading: boolean,
): string {
  if (isLoading) return 'Checking Gmail';
  if (status?.connected) return status.syncEnabled ? 'Gmail connected' : 'Gmail connected, sync off';
  if (status?.reconnectRequired) return 'Reconnect Gmail';
  if (status?.error) return 'Gmail status issue';
  return 'Gmail disconnected';
}

function formatSettingsDate(value: string): string {
  const time = Date.parse(value);
  if (Number.isNaN(time)) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(time));
}

function DesignSystemCard() {
  const { isModern, envForced } = useDesignSystem();

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <FlaskConical className="h-4 w-4 text-muted-foreground" aria-hidden />
            Experimental Modern UI
          </h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Switch the portal between the current design (Legacy) and the experimental One Up Modern
            UI. Saved on this device only. This changes presentation only — your data, actions, and
            permissions are unchanged.
          </p>
        </div>
        <Badge variant={isModern ? 'default' : 'muted'}>
          Current: {isModern ? 'Modern' : 'Legacy'}
        </Badge>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <DesignSystemToggle />
        <Button asChild size="sm" variant="outline" className="h-8 gap-1.5">
          <Link to="/design-lab">
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            Open Design Lab
          </Link>
        </Button>
      </div>

      <p className="mt-3 text-[11px] text-muted-foreground">
        {envForced
          ? 'Modern UI is force-enabled by VITE_ENABLE_MODERN_UI for this build.'
          : 'Modern UI is opt-in and off by default. Real pages keep using the current design until each one is migrated behind this switch.'}
      </p>
    </Card>
  );
}

function AiSettingsCard() {
  const modelId = useAiSettingsStore((state) => state.modelId);
  const setModelId = useAiSettingsStore((state) => state.setModelId);
  const current = resolveAiModelOption(modelId);

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
            Alice / AI Settings
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Choose the model Alice uses for chat, drafting, summaries, and context-aware assistance.
          </p>
        </div>
        <Badge variant="muted">Current: {current.label}</Badge>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        {AI_MODEL_OPTIONS.map((option) => {
          const active = option.id === current.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setModelId(option.id)}
              aria-pressed={active}
              className={cn(
                'rounded-lg border px-3 py-2.5 text-left transition-colors',
                active
                  ? 'border-primary/40 bg-primary/[0.04] shadow-[inset_0_2px_0_hsl(var(--primary))]'
                  : 'border-border/70 bg-card hover:border-foreground/15 hover:bg-muted/20',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold text-foreground">{option.label}</span>
                {active && <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden />}
              </div>
              <p className="mt-1 text-[11px] leading-4 text-muted-foreground">{option.description}</p>
            </button>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] text-muted-foreground">
        Saved on this device. The Worker validates the selected model against an allowlist before
        each request, so Alice never sends an unsupported model. API keys stay server-side.
      </p>
    </Card>
  );
}

function ShortcutKeys({ children }: { children: string }) {
  return (
    <kbd className="inline-flex min-h-6 items-center rounded-md border border-border bg-background px-2 py-0.5 font-mono text-[11px] font-medium text-foreground">
      {children}
    </kbd>
  );
}
